import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CalendarDays, Check, Copy, Hourglass, Share2, Users } from "lucide-react";
import { toast } from "sonner";
import PublicLayout from "@/components/site/PublicLayout";
import { IconAvatar } from "@/components/app/IconAvatar";
import { answerCohortPartnerRequest, apiAssetUrl, getMyCohorts, getPublicCohorts, isAuthenticated, joinCohort, leaveCohort } from "@/lib/api";
import { clearCohortJoin, pendingCohortJoin, rememberCohortJoin, rememberReturn } from "@/lib/returnTo";

const STEPS = [
  ["Join before the start date", "Come alone and we'll match you, bring your current partner, or invite a friend to join with you."],
  ["We pair you by hand", "On start day the team reads every profile and pairs everyone who came alone on goal, rhythm and timezone."],
  ["Start together", "Every pair opens a shared workspace on the same day: check-ins, tasks and a focus room."],
];

export const cohortDay = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
const inviteUrl = (code) => `${window.location.origin}/invite/${code}`;

export function cohortStatusLine(cohort) {
  if (cohort.joinable) return `${cohort.spotsLeft} of ${cohort.capacity} spots left`;
  if (cohort.status === "running") return "Under way";
  if (cohort.status === "open" && cohort.spotsLeft === 0) return "Full";
  return "Signups closed";
}

function ModeOption({ value, mode, setMode, title, body, disabled, note }) {
  const selected = mode === value;
  return (
    <label className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${disabled ? "cursor-not-allowed opacity-50" : ""} ${selected ? "border-[#F59E0B] bg-[#F59E0B]/[0.06]" : "border-white/10 hover:border-white/25"}`}>
      <input type="radio" name="cohort-mode" className="mt-1 accent-[#F59E0B]" checked={selected} disabled={disabled} onChange={() => setMode(value)} />
      <span>
        <span className="block text-sm text-[#F5F5F0]">{title}</span>
        <span className="block text-xs text-[#A1A1AA]">{disabled && note ? note : body}</span>
      </span>
    </label>
  );
}

function CohortCard({ cohort, signedIn, focused, onChanged, partners, limitReached, otherCohorts = [], maxCohorts = 1 }) {
  const navigate = useNavigate();
  const [note, setNote] = useState(cohort.membership?.goalNote || "");
  const [joining, setJoining] = useState(false);
  const [busy, setBusy] = useState(false);
  const newPartnerBlocked = limitReached && !cohort.allowOverLimit;
  const [mode, setMode] = useState(newPartnerBlocked && partners.length ? "partner" : "match");
  const [partnershipId, setPartnershipId] = useState(partners[0]?.partnershipId || "");
  const member = cohort.membership;
  const request = cohort.partnerRequest;
  const filled = Math.min(100, Math.round((cohort.memberCount / Math.max(1, cohort.capacity)) * 100));
  const path = `/cohorts/${cohort.slug}`;
  const twoSeats = mode !== "match";
  const cover = apiAssetUrl(cohort.imageUrl);

  const join = async () => {
    setBusy(true);
    try {
      const result = await joinCohort(cohort.id, { goalNote: note.trim(), mode, partnershipId: mode === "partner" ? partnershipId : null });
      if (mode === "partner") toast("Request sent.", { description: `${result.membership?.partner?.fullName || "Your partner"} confirms from their cohort page. Their seat is held.` });
      else if (mode === "friend") toast("You're in. Now send your friend the invite.", { description: "When they accept, you two are paired for this cohort." });
      else toast(`You're in ${cohort.name}.`, { description: `We'll pair you by ${cohortDay(cohort.startsOn)} and let you know here and by notification.` });
      setJoining(false);
      onChanged();
    } catch (error) {
      if (error.code === "profile_incomplete") {
        rememberReturn(path);
        if (mode === "match") rememberCohortJoin(cohort.slug);
        toast("Finish your profile first.", { description: "Your seat is saved. You'll be added as soon as it's done." });
        navigate("/onboarding");
      } else if (error.code === "solo_mode") {
        toast(error.message, { action: { label: "Edit profile", onClick: () => navigate("/onboarding?edit=1") } });
      } else {
        toast("Could not join this cohort.", { description: error.message });
      }
    } finally {
      setBusy(false);
    }
  };

  const answer = async (decision) => {
    setBusy(true);
    try {
      const result = await answerCohortPartnerRequest(cohort.id, decision);
      toast(decision === "accept" ? `You're doing ${cohort.name} with ${request.fullName.split(" ")[0]}.` : "Request declined.", decision === "accept" ? { description: `You both start on ${cohortDay(cohort.startsOn)}.` } : undefined);
      if (result) onChanged();
    } catch (error) {
      toast("Could not answer the request.", { description: error.message });
    } finally {
      setBusy(false);
    }
  };

  const leave = async () => {
    if (!window.confirm(`Leave ${cohort.name}? Your spot goes to someone else.`)) return;
    setBusy(true);
    try { await leaveCohort(cohort.id); toast("You left the cohort."); onChanged(); }
    catch (error) { toast("Could not leave the cohort.", { description: error.message }); }
    finally { setBusy(false); }
  };

  const copyInvite = async (code) => {
    try { await navigator.clipboard.writeText(inviteUrl(code)); toast("Invite link copied."); } catch { toast(inviteUrl(code)); }
  };
  const shareInvite = async (code) => {
    const text = `Do ${cohort.name} on HustlrMate with me. We start ${cohortDay(cohort.startsOn)}. Accept here: ${inviteUrl(code)} (or enter code ${code} in the app)`;
    if (navigator.share) { try { await navigator.share({ title: cohort.name, text, url: inviteUrl(code) }); return; } catch { /* dismissed */ } }
    try { await navigator.clipboard.writeText(text); toast("Invite message copied."); } catch { toast(text); }
  };

  const startJoin = () => {
    if (!signedIn) {
      // New visitors sign up and onboard first; the cohort page joins them automatically when they return.
      rememberReturn(path);
      rememberCohortJoin(cohort.slug);
      navigate("/auth");
      return;
    }
    setJoining(true);
  };

  const saveNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await joinCohort(cohort.id, { goalNote: note.trim() }); toast("Saved. That helps us pair you well."); onChanged(); }
    catch (error) { toast("Could not save your note.", { description: error.message }); }
    finally { setBusy(false); }
  };

  return (
    <article id={cohort.slug} className={`hm-card scroll-mt-28 rounded-md p-6 sm:p-8 ${cover ? "grid gap-6 sm:grid-cols-[minmax(0,220px)_1fr] sm:gap-8" : ""} ${focused ? "ring-1 ring-[#F59E0B]/60" : ""}`} data-testid={`cohort-${cohort.slug}`}>
      {/* Portrait cover leads on the left, like a poster; on phones it sits on top. */}
      {cover && (
        <div className="relative mx-auto w-full max-w-[260px] self-start overflow-hidden rounded-md border border-white/10 bg-[#121212] sm:mx-0 sm:max-w-none">
          <img src={cover} alt="" loading="lazy" className="block aspect-[4/5] w-full object-cover" />
          {cohort.unlockAllFeatures && <span className="absolute bottom-2 left-2 rounded-sm bg-black/70 px-2 py-1 text-[9px] uppercase tracking-[0.16em] text-[#F59E0B] backdrop-blur">All features unlocked</span>}
        </div>
      )}
      <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        {cohort.niche && <span className="rounded-sm border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] text-[#F59E0B]">{cohort.niche}</span>}
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A1AA]">{cohortStatusLine(cohort)}</span>
      </div>
      <h2 className="mt-4 font-serif-editorial text-3xl font-light leading-tight tracking-tight lining-nums sm:text-4xl">{cohort.name}</h2>
      {cohort.tagline && <p className="mt-2 text-[#D4D4D8]">{cohort.tagline}</p>}
      <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#A1A1AA]">
        <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4" /> {cohortDay(cohort.startsOn)} – {cohortDay(cohort.endsOn)} · {cohort.lengthDays} days</span>
        <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> {cohort.memberCount} joined</span>
        {cohort.joinable && cohort.signupClosesAt && <span>Signups close {new Date(cohort.signupClosesAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</span>}
      </div>
      <div className="mt-4 h-1 overflow-hidden rounded-full bg-white/[0.06]" aria-hidden="true"><div className="h-full rounded-full bg-[#F59E0B]" style={{ width: `${filled}%` }} /></div>
      {cohort.description && <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-[#A1A1AA]">{cohort.description}</p>}

      <div className="mt-7">
        {member?.status === "paired" ? (
          <div className="flex flex-wrap items-center gap-4 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/[0.06] p-4">
            {member.partner && <IconAvatar iconKey={member.partner.profileIcon} size="md" />}
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#F5F5F0]">You're doing this cohort with {member.partner?.fullName || "your partner"}.</div>
              <div className="text-xs text-[#A1A1AA]">{member.joinMode === "match" ? "The team paired you. Your Starter Sprint is running." : `You both start on ${cohortDay(cohort.startsOn)}.`}</div>
            </div>
            {member.partnershipId && <Link to={`/workspace/${member.partnershipId}`} className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-semibold">Open workspace <ArrowRight className="h-3.5 w-3.5" /></Link>}
          </div>
        ) : member?.status === "awaiting_partner" ? (
          <div className="rounded-md border border-white/10 bg-black/20 p-4">
            <div className="flex items-start gap-3">
              <Hourglass className="mt-0.5 h-4 w-4 text-[#F59E0B]" />
              <div className="min-w-0 flex-1">
                {member.joinMode === "partner" ? (
                  <>
                    <div className="text-sm text-[#F5F5F0]">Waiting for {member.partner?.fullName || "your partner"} to confirm.</div>
                    <div className="mt-1 text-xs text-[#A1A1AA]">They'll see your request on this page and in their notifications. Their seat is held.</div>
                  </>
                ) : (
                  <>
                    <div className="text-sm text-[#F5F5F0]">Send your friend this invite. Their seat is held until they accept.</div>
                    {member.inviteCode && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <span className="rounded-md border border-white/10 px-3 py-1.5 font-mono text-sm tracking-[0.2em] text-[#F5F5F0]">{member.inviteCode}</span>
                        <button onClick={() => copyInvite(member.inviteCode)} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"><Copy className="h-3.5 w-3.5" /> Copy link</button>
                        <button onClick={() => shareInvite(member.inviteCode)} className="hm-btn-ghost inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs"><Share2 className="h-3.5 w-3.5" /> Share</button>
                      </div>
                    )}
                  </>
                )}
              </div>
              {["open", "closed"].includes(cohort.status) && <button onClick={leave} disabled={busy} className="text-xs text-[#A1A1AA] underline-offset-4 hover:text-[#F5F5F0] hover:underline">Leave</button>}
            </div>
          </div>
        ) : member ? (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-white/10 bg-black/20 p-4">
            <div className="flex items-start gap-3">
              <Check className="mt-0.5 h-4 w-4 text-[#F59E0B]" />
              <div>
                <div className="text-sm text-[#F5F5F0]">You're in. We'll pair you by {cohortDay(cohort.startsOn)}.</div>
                {member.goalNote && <div className="mt-1 text-xs text-[#A1A1AA]">Your note: “{member.goalNote}”</div>}
              </div>
            </div>
            {["open", "closed"].includes(cohort.status) && <button onClick={leave} disabled={busy} className="text-xs text-[#A1A1AA] underline-offset-4 hover:text-[#F5F5F0] hover:underline">Leave cohort</button>}
            {!member.goalNote && (
              <div className="w-full border-t border-white/5 pt-3">
                <label className="block text-xs text-[#A1A1AA]">
                  One line: what do you want to finish by {cohortDay(cohort.endsOn)}? <span className="text-[#777]">· helps us pair you</span>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <input value={note} maxLength={240} onChange={(event) => setNote(event.target.value)} placeholder="e.g. Finish two full CAT mocks a week and review every one." className="hm-input min-w-0 flex-1 rounded-md px-3 py-2 text-sm" data-testid="cohort-note" />
                    <button onClick={saveNote} disabled={busy || !note.trim()} className="hm-btn-primary rounded-md px-4 py-2 text-xs font-semibold disabled:opacity-60">Save</button>
                  </div>
                </label>
              </div>
            )}
          </div>
        ) : request ? (
          <div className="flex flex-wrap items-center gap-4 rounded-md border border-[#F59E0B]/30 bg-[#F59E0B]/[0.06] p-4" data-testid="cohort-partner-request">
            <IconAvatar iconKey={request.profileIcon} size="md" />
            <div className="min-w-0 flex-1">
              <div className="text-sm text-[#F5F5F0]">{request.fullName} wants to do this cohort with you.</div>
              <div className="text-xs text-[#A1A1AA]">You keep your current partnership and start the cohort together on {cohortDay(cohort.startsOn)}.</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => answer("accept")} disabled={busy} className="hm-btn-primary rounded-md px-4 py-2 text-xs font-semibold">Join together</button>
              <button onClick={() => answer("decline")} disabled={busy} className="hm-btn-ghost rounded-md px-4 py-2 text-xs">Decline</button>
            </div>
          </div>
        ) : !cohort.joinable ? (
          <p className="text-sm text-[#A1A1AA]">{cohort.status === "running" ? "This cohort has started." : "This cohort isn't taking new members."} Invite someone you know from Matches, or watch for the next cohort.</p>
        ) : joining ? (
          <div className="space-y-4 rounded-md border border-white/10 bg-black/20 p-4">
            <div className="space-y-2" role="radiogroup" aria-label="How you'll join">
              <ModeOption value="match" mode={mode} setMode={setMode} title="Match me with someone" body="The team pairs you with another member on start day." disabled={newPartnerBlocked} note="Your plan's partnership limit is reached and this cohort doesn't go past it." />
              {partners.length > 0 && <ModeOption value="partner" mode={mode} setMode={setMode} title="With my current partner" body="They confirm, and you two do the cohort together. Holds a seat for them." />}
              <ModeOption value="friend" mode={mode} setMode={setMode} title="With a friend" body="You get an invite link. When they accept, you're partners for the cohort. Holds a seat for them." disabled={newPartnerBlocked} note="Your plan's partnership limit is reached and this cohort doesn't go past it." />
            </div>
            {mode === "partner" && partners.length > 1 && (
              <label className="block text-xs text-[#A1A1AA]">Which partner?
                <select value={partnershipId} onChange={(event) => setPartnershipId(event.target.value)} className="hm-input mt-1 w-full rounded-md px-3 py-2 text-sm text-[#F5F5F0]">
                  {partners.map((partner) => <option key={partner.partnershipId} value={partner.partnershipId}>{partner.fullName}</option>)}
                </select>
              </label>
            )}
            {twoSeats && cohort.spotsLeft < 2 && <p className="text-xs text-[#F59E0B]">Only one spot is left, so there's no room to bring someone.</p>}
            <label className="block text-xs text-[#A1A1AA]">
              What do you want to finish by {cohortDay(cohort.endsOn)}? <span className="text-[#777]">· helps us pair you</span>
              <textarea value={note} maxLength={240} rows={2} onChange={(event) => setNote(event.target.value)} placeholder="e.g. Finish two full CAT mocks a week and review every one." className="hm-input mt-2 w-full rounded-md px-3 py-2 text-sm" />
            </label>
            <div className="flex flex-wrap gap-2">
              <button onClick={join} disabled={busy || (newPartnerBlocked && mode !== "partner") || (twoSeats && cohort.spotsLeft < 2)} data-testid="cohort-join-confirm" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
                {busy ? "Joining…" : mode === "partner" ? "Ask my partner" : mode === "friend" ? "Join and get invite link" : "Join cohort"}
              </button>
              <button onClick={() => setJoining(false)} className="hm-btn-ghost rounded-md px-4 py-2.5 text-sm">Cancel</button>
            </div>
          </div>
        ) : signedIn && !cohort.canJoin && otherCohorts.length >= maxCohorts ? (
          <p className="text-sm text-[#A1A1AA]" data-testid="cohort-limit-note">You're already in {otherCohorts.map((other) => other.name).join(", ")}. You can be in {maxCohorts} cohort{maxCohorts === 1 ? "" : "s"} at a time, so join this one after that ends (or leave it before it starts).</p>
        ) : signedIn && !cohort.canJoin ? (
          <p className="text-sm text-[#A1A1AA]">Your plan's active-partnership limit is reached, and this cohort doesn't go past it. End a partnership or upgrade to join.</p>
        ) : (
          <button onClick={startJoin} data-testid="cohort-join" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold">
            {signedIn ? "Join this cohort" : "Join — create a free account"} <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {!signedIn && cohort.joinable && <p className="mt-3 text-xs text-[#7c7c84]">Sign up, finish your profile, and you're added to this cohort automatically.</p>}
        {cohort.unlockAllFeatures && <p className="mt-3 text-xs text-[#A1A1AA]">Free plan? Every pair in this cohort still gets the full workspace: sessions, focus room, shared tasks and unlimited partner chat.</p>}
      </div>
      </div>
    </article>
  );
}

export default function Cohorts() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const signedIn = isAuthenticated();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    // An expired session still sees the public list rather than an error.
    (signedIn ? getMyCohorts().catch((caught) => (caught.status === 401 ? getPublicCohorts() : Promise.reject(caught))) : getPublicCohorts())
      .then((result) => { setData(result); setError(""); })
      .catch((caught) => setError(caught.message || "Cohorts could not be loaded."));
  }, [signedIn]);
  useEffect(() => { load(); }, [load]);

  const cohorts = data?.items || null;
  // From backend config (max_active_cohorts_per_user); live cohorts only, finished ones don't count.
  const maxCohorts = data?.maxActiveCohorts || 1;
  const activeCohorts = data?.activeCohorts || [];
  const autoJoinTried = useRef(false);
  useEffect(() => {
    if (!signedIn || !data?.activePartners || autoJoinTried.current) return;
    const pending = pendingCohortJoin();
    if (!pending) return;
    autoJoinTried.current = true;
    const cohort = (data.items || []).find((item) => item.slug === pending);
    if (!cohort || cohort.membership) { clearCohortJoin(); return; }
    if (!cohort.canJoin) {
      clearCohortJoin();
      toast(`${cohort.name} isn't taking new members right now.`, { description: cohort.spotsLeft === 0 ? "It filled up while you were signing up." : "Signups are closed." });
      return;
    }
    joinCohort(cohort.id, { mode: "match" })
      .then(() => {
        clearCohortJoin();
        toast(`You're in ${cohort.name}.`, { description: "Add one line about your goal below so we can pair you well." });
        load();
      })
      .catch((caught) => {
        // Profile and solo-mode problems keep the join pending, so it completes once they're fixed.
        if (caught.code === "profile_incomplete") { rememberReturn(`/cohorts/${pending}`); navigate("/onboarding"); return; }
        if (caught.code === "solo_mode") { rememberReturn(`/cohorts/${pending}`); toast(caught.message, { action: { label: "Edit profile", onClick: () => navigate("/onboarding?edit=1") } }); return; }
        clearCohortJoin();
        toast(`Could not add you to ${cohort.name}.`, { description: caught.message });
      });
  }, [signedIn, data, load, navigate]);

  useEffect(() => {
    if (slug && cohorts?.length) document.getElementById(slug)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [slug, cohorts]);

  const ordered = cohorts ? [...cohorts].sort((a, b) => (b.slug === slug) - (a.slug === slug)) : [];

  return (
    <PublicLayout>
      <div className="mx-auto max-w-4xl px-6 sm:px-10">
        <div className="overline mb-4">Cohorts</div>
        <h1 className="font-serif-editorial text-5xl font-light leading-[1.02] tracking-tight sm:text-6xl text-balance">
          Start on the same day as <em className="italic text-[#F59E0B]">someone who means it.</em>
        </h1>
        <p className="mt-5 max-w-2xl text-[#A1A1AA]">A cohort is a group that starts together. Everyone joins before the start date; whoever comes alone is paired one by one, by hand. Nobody opens an empty app.</p>
        <p className="mt-3 max-w-2xl text-xs text-[#7c7c84]" data-testid="cohort-rule">
          You can be in {maxCohorts} cohort{maxCohorts === 1 ? "" : "s"} at a time{activeCohorts.length ? <>; you're in <span className="text-[#F5F5F0]">{activeCohorts.map((item) => item.name).join(", ")}</span></> : ""}. Once a cohort ends, you're free to join the next one.
        </p>

        <ol className="mt-10 grid gap-4 sm:grid-cols-3">
          {STEPS.map(([title, body], index) => (
            <li key={title} className="border-t border-white/10 pt-4">
              <div className="font-serif-editorial text-2xl text-[#F59E0B]">{["I", "II", "III"][index]}</div>
              <div className="mt-2 text-sm text-[#F5F5F0]">{title}</div>
              <p className="mt-1 text-xs leading-relaxed text-[#A1A1AA]">{body}</p>
            </li>
          ))}
        </ol>

        <div className="mt-14 space-y-6">
          {error && <div className="hm-card rounded-md p-6 text-sm text-[#A1A1AA]">{error}</div>}
          {!cohorts && !error && <div className="text-sm text-[#A1A1AA]">Loading cohorts…</div>}
          {cohorts?.length === 0 && (
            <div className="hm-card rounded-md p-8">
              <h2 className="font-serif-editorial text-3xl font-light">No cohort is open right now.</h2>
              <p className="mt-3 text-sm text-[#A1A1AA]">The next one is announced here first. Meanwhile, use the free focus timer, or sign up and invite someone you already know.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/focus" className="hm-btn-ghost rounded-md px-5 py-2.5 text-sm">Free focus timer</Link>
                <Link to={signedIn ? "/matches#invite" : "/auth"} className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold">{signedIn ? "Invite a partner" : "Create an account"} <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          )}
          {ordered.map((cohort) => (
            <CohortCard key={cohort.id} cohort={cohort} signedIn={signedIn} focused={cohort.slug === slug} onChanged={load}
              partners={data?.activePartners || []} limitReached={Boolean(data?.partnershipLimitReached)}
              maxCohorts={maxCohorts} otherCohorts={activeCohorts.filter((item) => item.id !== cohort.id)} />
          ))}
        </div>
      </div>
    </PublicLayout>
  );
}
